import { z } from 'zod';
import { PropertyStatus, InvestmentStrategy, DealStatus, TaskPriority, TaskStatus, RenovationStatus, RenovationType } from '../types';

// --- PROPERTY SCHEMA ---
export const propertySchema = z.object({
  address: z.string().min(5, "Address requires at least 5 characters"),
  city: z.string().min(2, "City is required"),
  zipCode: z.string().optional(),
  lat: z.coerce.number().optional(),
  lng: z.coerce.number().optional(),
  purchasePrice: z.coerce.number().min(0),
  currentValue: z.coerce.number().min(0),
  monthlyRent: z.coerce.number().min(0),
  expenses: z.coerce.number().min(0),
  status: z.nativeEnum(PropertyStatus),
  strategy: z.nativeEnum(InvestmentStrategy),
  loanAmount: z.coerce.number().optional(),
  interestRate: z.coerce.number().optional(),
  imageUrl: z.union([z.string().url(), z.literal('')]).optional(),
  insuranceExpiry: z.string().optional(),
});
export type PropertyFormData = z.infer<typeof propertySchema>;

// --- TENANT SCHEMA ---
export const tenantSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(9, "Phone number is too short"),
  idNumber: z.string().optional(),
  nif: z.string().optional(),
  status: z.enum(['Active', 'Past', 'Lead']),
  rentAmount: z.coerce.number().min(0),
  depositAmount: z.coerce.number().min(0),
  paymentDay: z.coerce.number().min(1).max(31).optional(),
  leaseStart: z.string().optional(),
  leaseEnd: z.string().optional(),
  currentPropertyId: z.string().optional(),
  notes: z.string().optional(),
});
export type TenantFormData = z.infer<typeof tenantSchema>;

// --- DEAL SCHEMA ---
export const dealSchema = z.object({
  address: z.string().min(5, "Address is required"),
  city: z.string().min(2, "City is required"),
  askingPrice: z.coerce.number().min(0),
  offerPrice: z.coerce.number().optional(),
  estRent: z.coerce.number().min(0),
  estRenovation: z.coerce.number().min(0),
  status: z.nativeEnum(DealStatus),
  notes: z.string().optional(),
  imageUrl: z.union([z.string().url(), z.literal('')]).optional(),
});
export type DealFormData = z.infer<typeof dealSchema>;

// --- MAINTENANCE SCHEMA ---
export const maintenanceSchema = z.object({
  title: z.string().min(3, "Title is too short"),
  description: z.string().min(5, "Description is required"),
  propertyId: z.string().min(1, "You must select a property"),
  priority: z.nativeEnum(TaskPriority),
  status: z.nativeEnum(TaskStatus),
  costEstimate: z.coerce.number().min(0, "Cost cannot be negative"),
  dateCreated: z.string().optional(),
});
export type MaintenanceFormData = z.infer<typeof maintenanceSchema>;

// --- RENOVATION PROJECT SCHEMA (NOVO) ---
export const renovationProjectSchema = z.object({
  name: z.string().min(3, "Project Name is required"),
  propertyId: z.string().min(1, "Property is required"),
  type: z.nativeEnum(RenovationType),
  status: z.nativeEnum(RenovationStatus),
  startDate: z.string().min(1, "Start Date is required"),
  endDateEstimated: z.string().min(1, "End Date is required"),
  budget: z.coerce.number().min(0, "Budget cannot be negative"),
  description: z.string().optional(),
});
export type RenovationFormData = z.infer<typeof renovationProjectSchema>;

// --- CONTRACTOR SCHEMA (NOVO) ---
export const contractorSchema = z.object({
  name: z.string().min(2, "Name is required"),
  specialty: z.string().min(2, "Specialty is required"),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().optional(),
  hourlyRate: z.coerce.number().min(0).optional(),
});
export type ContractorFormData = z.infer<typeof contractorSchema>;